import { ReviewerRole } from '../types'
import { Value as RecipientsSelectorValue } from 'components/ShareSheet/RecipientSelector/Types'
import { synchronizeRolesIn } from './utils'
import { RecipientSelectorField, RecipientSelectorFieldProps } from '../RecipientsSelectorField/RecipientsSelectorField'
import { ForwardedRef, forwardRef, Fragment, ReactElement } from 'react'

interface TypedRecipientSelectorField extends Omit<RecipientSelectorFieldProps, 'id'> {}

const ReviewersField = forwardRef<HTMLDivElement, TypedRecipientSelectorField>((props, fRef) => (
  <RecipientSelectorField {...props} id="reviewers-select" ref={fRef} />
))

const ApproversField = forwardRef<HTMLDivElement, TypedRecipientSelectorField>((props, fRef) => (
  <RecipientSelectorField {...props} id="approvers-select" ref={fRef} />
))

const ViewersField = forwardRef<HTMLDivElement, TypedRecipientSelectorField>((props, fRef) => (
  <RecipientSelectorField {...props} id="viewers-select" ref={fRef} />
))

interface Props {
  roles: ReviewerRole[]
  onReviewersChange: (reviewers: RecipientsSelectorValue[]) => void
  onApproversChange: (approvers: RecipientsSelectorValue[]) => void
  onViewersChange: (viewers: RecipientsSelectorValue[]) => void
  reviewers: RecipientsSelectorValue[]
  approvers: RecipientsSelectorValue[]
  viewers: RecipientsSelectorValue[]
  reviewerRolesButtons?: ReactElement
  reviewerRolesDropdown?: ReactElement
  autoFocus?: boolean
  disabled?: boolean
  allowEmails?: boolean
  onRecipientActionClick?: () => void
  placeholder?: string
  reviewersLabel?: string
  approversLabel?: string
  viewersLabel?: string
  reviewersLabelClassNames?: string
  approversLabelClassNames?: string
  viewersLabelClassNames?: string
  recipientsClassNames?: string
}

const ReviewersFields = (
  {
    roles,
    onReviewersChange,
    onApproversChange,
    onViewersChange,
    approvers,
    reviewers,
    viewers,
    reviewerRolesButtons,
    reviewerRolesDropdown,
    autoFocus = true,
    disabled = false,
    onRecipientActionClick,
    allowEmails = true,
    placeholder,
    reviewersLabel = 'Reviewers',
    approversLabel = 'Approvers',
    viewersLabel = 'Viewers',
    reviewersLabelClassNames,
    approversLabelClassNames,
    viewersLabelClassNames,
    recipientsClassNames,
  }: Props,
  recipientsSelectorRef: ForwardedRef<HTMLDivElement>,
) => {
  const handleReviewersSelectorChange = (value: RecipientsSelectorValue[]) =>
    synchronizeRolesIn([value, approvers, viewers], [onReviewersChange, onApproversChange, onViewersChange])

  const handleApproversSelectorChange = (value: RecipientsSelectorValue[]) =>
    synchronizeRolesIn([value, reviewers, viewers], [onApproversChange, onReviewersChange, onViewersChange])

  const handleViewersSelectorChange = (value: RecipientsSelectorValue[]) =>
    synchronizeRolesIn([value, reviewers, approvers], [onViewersChange, onReviewersChange, onApproversChange])

  const focusApprovers = autoFocus && roles.includes(ReviewerRole.APPROVER) && approvers.length < 1
  const focusReviewers = autoFocus && roles.includes(ReviewerRole.REVIEWER) && reviewers.length < 1 && !focusApprovers

  const roleSelectorsMap = {
    [ReviewerRole.REVIEWER]: {
      component: ReviewersField,
      props: {
        autoFocus: focusReviewers,
        onChange: handleReviewersSelectorChange,
        recipients: reviewers,
        ref: recipientsSelectorRef,
        disabled,
        allowEmails,
        placeholder,
        onRecipientActionClick,
        label: reviewersLabel,
        labelClassNames: reviewersLabelClassNames,
        recipientsClassNames,
        reviewerRolesDropdown,
      },
    },
    [ReviewerRole.APPROVER]: {
      component: ApproversField,
      props: {
        autoFocus: focusApprovers,
        onChange: handleApproversSelectorChange,
        recipients: approvers,
        disabled,
        allowEmails,
        placeholder,
        onRecipientActionClick,
        label: approversLabel,
        labelClassNames: approversLabelClassNames,
      },
    },
    [ReviewerRole.VIEWER]: {
      component: ViewersField,
      props: {
        onChange: handleViewersSelectorChange,
        recipients: viewers,
        disabled,
        allowEmails,
        placeholder,
        label: viewersLabel,
        labelClassNames: viewersLabelClassNames,
      },
    },
  }

  return (
    <>
      {roles.map((role, index) => {
        const { component: Component, props } = roleSelectorsMap[role]
        return (
          <Fragment key={role}>
            <Component key={role} {...props} />
            {index === 0 && reviewerRolesButtons}
          </Fragment>
        )
      })}
      {roles.length < 1 && reviewerRolesButtons}
    </>
  )
}

const ReviewersFieldsWithRef = forwardRef<HTMLDivElement, Props>(ReviewersFields)

export { ReviewersFieldsWithRef as ReviewersFields }
